import type { PontoDoAnalitoResponse, SerieDoAnalitoResponse } from '@/api/generated/model';
import { ehExterno, LEGENDA_DE_EXTERNO, rotuloDeExterno } from '@/features/historico/pontoExterno';

/**
 * Cinco cores, na mesma ordem que a Análise Comparativa usa.
 *
 * São os tokens `--chart-N` do `index.css` — triplas HSL cruas, daí o `hsl()` em
 * volta. É isso que faz o tema escuro trocar a paleta sozinho.
 *
 * ⚠️ Aqui a cor identifica o **analito**, não a interpretação — é a dimensão
 * agrupada, e sem cor por série não há como ler qual barra é qual. A regra "cor
 * nenhuma afirma que subir é ruim" continua valendo: quem marca o valor fora da
 * faixa é o contorno âmbar, que vem da `interpretacao` gravada pelo servidor.
 */
const CORES = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

/**
 * Coordenadas do `viewBox`. São unidades internas — o SVG escala sozinho.
 *
 * ⚠️ **720 é a largura BASE, não a largura das barras somadas.** O `viewBox`
 * cresce junto com o conteúdo (`w-full` + `overflow-x-auto`), então a proporção
 * entre a largura do desenho e a da tela é o fator de escala de TUDO, texto
 * incluído. Dimensionar o quadro pelas barras (3 datas ≈ 230 unidades num
 * contêiner de 1400px) ampliava o gráfico 6× e imprimia a data em 60px. Fixar a
 * base nos mesmos 720 do `GraficoDeEvolucao` ao lado mantém os dois na mesma
 * escala; quem se ajusta é a barra, não o quadro.
 */
const LARGURA_BASE = 720;
const MARGEM = { topo: 16, direita: 16, esquerda: 48 };
const AREA_ALTURA = 196;

/**
 * Limites da barra. O mínimo é legibilidade; o máximo evita que duas datas
 * virem dois paredões — acima disso a barra deixa de ser barra.
 */
const BARRA = { minima: 8, maxima: 44, entre: 3, entreGrupos: 24 };

/** Marca do resultado sem número, para o lugar da barra não sumir do grupo. */
const ALTURA_DO_VAZIO = 2;

interface Props {
  /** Analitos do MESMO exame do catálogo, na ordem do laudo. */
  series: SerieDoAnalitoResponse[];
}

/** Um exame lançado: a data e o que cada analito mediu nele. */
interface Grupo {
  exameId: string;
  medidoEm: string;
  /** Mesma ordem de `series`; `null` = aquele analito não veio neste exame. */
  valores: (PontoDoAnalitoResponse | null)[];
}

/**
 * Barras agrupadas: uma barra por analito, em cada data.
 *
 * É o gráfico do exame que rende **mais de um resultado** — "Cortisol Basal e
 * Pós-ACTH" é o caso didático: as duas medições vêm do mesmo tubo, no mesmo dia,
 * e o que interessa é a relação entre elas, que duas linhas separadas escondem.
 * Para exame de resultado único, quem desenha continua sendo o
 * `GraficoDeEvolucao` — evolução de um valor é linha, não barra.
 *
 * ⚠️ **Um grupo por EXAME, não por dia.** A chave é o `exameId`, e não a data
 * formatada: dois exames do mesmo catálogo colhidos no mesmo dia (recoleta,
 * segunda dosagem) são dois eventos clínicos, e fundi-los faria uma barra
 * sobrescrever a outra em silêncio. Aparecem como dois grupos com o mesmo
 * rótulo, que é a verdade.
 *
 * ⚠️ **A base do eixo é zero** (ou o mínimo, se houver valor negativo). Barra que
 * começa recortada exagera a diferença entre resultados próximos — é o vício
 * clássico do gráfico de barras, e num laudo ele viraria diferença clínica que
 * não existe. O `GraficoDeEvolucao`, sendo linha, pode se dar ao luxo de
 * enquadrar a faixa.
 *
 * ⚠️ **SVG à mão, como o `GraficoDeEvolucao` ao lado.** O `recharts` está no
 * projeto, mas sob `React.lazy` e só na Análise Comparativa; o histórico é rota
 * do bundle principal, e o mesmo argumento de lá vale aqui.
 */
export function GraficoDeBarrasDoExame({ series }: Props) {
  const grupos = agrupar(series);
  const numeros = grupos.flatMap((grupo) =>
    grupo.valores.flatMap((ponto) => (ponto?.valor != null ? [ponto.valor] : [])),
  );

  // Exame só de resultado qualitativo ("REAGENTE", "AUSENTE") não tem barra a
  // desenhar. Sem esta saída sobrariam eixo e legenda em volta de nada.
  if (numeros.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        Nenhuma medição numérica para este exame.
      </p>
    );
  }

  const base = Math.min(0, ...numeros);
  const maximo = Math.max(...numeros);
  // Tudo igual à base (série toda em zero) tem amplitude zero e dividiria por
  // zero na escala.
  const teto = maximo > base ? maximo : base + 1;
  const escala = { base, teto: teto + (teto - base) * 0.12 };

  // A barra se ajusta para o conjunto ocupar a largura base. Só quando nem no
  // mínimo legível cabe é que o quadro cresce — e aí o contêiner rola.
  const areaBase = LARGURA_BASE - MARGEM.esquerda - MARGEM.direita;
  const porGrupo = (areaBase - (grupos.length - 1) * BARRA.entreGrupos) / grupos.length;
  const larguraDaBarra = Math.min(
    BARRA.maxima,
    Math.max(BARRA.minima, (porGrupo - (series.length - 1) * BARRA.entre) / series.length),
  );

  const larguraDoGrupo = series.length * larguraDaBarra + (series.length - 1) * BARRA.entre;
  const areaLargura = grupos.length * larguraDoGrupo + (grupos.length - 1) * BARRA.entreGrupos;
  const LARGURA = Math.max(LARGURA_BASE, MARGEM.esquerda + areaLargura + MARGEM.direita);

  // Rótulo de data na diagonal quando o grupo é estreito: na horizontal,
  // "01/08/26" de um grupo encosta no do vizinho e vira borrão.
  const rotuloNaDiagonal = larguraDoGrupo < 58;
  const margemDaBase = rotuloNaDiagonal ? 48 : 28;
  const ALTURA = MARGEM.topo + AREA_ALTURA + margemDaBase;

  // Com poucas datas sobra faixa: centralizar é melhor que empilhar tudo à
  // esquerda com metade do eixo vazia.
  const inicio =
    MARGEM.esquerda + Math.max(0, (LARGURA - MARGEM.esquerda - MARGEM.direita - areaLargura) / 2);

  /** Canto esquerdo do grupo. */
  const xDoGrupo = (indice: number) => inicio + indice * (larguraDoGrupo + BARRA.entreGrupos);

  const y = (valor: number) =>
    MARGEM.topo +
    AREA_ALTURA -
    ((valor - escala.base) / (escala.teto - escala.base)) * AREA_ALTURA;

  const yDaBase = y(escala.base);
  const rotulos = [escala.teto, (escala.teto + escala.base) / 2, escala.base];

  return (
    <figure className="space-y-3">
      {/* A rolagem é do contêiner, não do SVG: com dez datas as barras ficariam
          fios de cabelo se o gráfico tentasse caber sempre na tela. */}
      <div className="overflow-x-auto">
        <svg
          viewBox={`0 0 ${LARGURA} ${ALTURA}`}
          style={{ minWidth: LARGURA }}
          className="w-full h-auto"
          role="img"
          aria-label={resumoAcessivel(series, grupos)}
        >
          {rotulos.map((valor) => (
            <g key={valor}>
              <line
                x1={MARGEM.esquerda}
                x2={LARGURA - MARGEM.direita}
                y1={y(valor)}
                y2={y(valor)}
                className="stroke-border"
                strokeWidth={1}
                strokeDasharray="3 3"
              />
              <text
                x={MARGEM.esquerda - 8}
                y={y(valor) + 4}
                textAnchor="end"
                className="fill-muted-foreground text-[10px]"
              >
                {formatar(valor)}
              </text>
            </g>
          ))}

          {/* Linha da base, contínua: é o zero de onde as barras saem. */}
          <line
            x1={MARGEM.esquerda}
            x2={LARGURA - MARGEM.direita}
            y1={yDaBase}
            y2={yDaBase}
            className="stroke-border"
            strokeWidth={1}
          />

          {grupos.map((grupo, indiceDoGrupo) => {
            const xGrupo = xDoGrupo(indiceDoGrupo);
            const centro = xGrupo + larguraDoGrupo / 2;
            const yRotulo = MARGEM.topo + AREA_ALTURA + 16;

            return (
              <g key={grupo.exameId}>
                {grupo.valores.map((ponto, indiceDaSerie) => {
                  const serie = series[indiceDaSerie];
                  if (!serie) return null;

                  const x = xGrupo + indiceDaSerie * (larguraDaBarra + BARRA.entre);
                  const cor = CORES[indiceDaSerie % CORES.length];
                  const foraDaFaixa =
                    ponto?.interpretacao === 'ABAIXO' || ponto?.interpretacao === 'ACIMA';

                  // Sem número não há barra: fica a marca rasa no lugar dela,
                  // para o grupo não perder o alinhamento e o resultado
                  // qualitativo ("REAGENTE") continuar alcançável no tooltip.
                  if (ponto?.valor == null) {
                    return (
                      <rect
                        key={serie.analitoId}
                        x={x}
                        y={yDaBase - ALTURA_DO_VAZIO}
                        width={larguraDaBarra}
                        height={ALTURA_DO_VAZIO}
                        className="fill-muted-foreground/40"
                      >
                        <title>{tooltip(serie, ponto, grupo.medidoEm)}</title>
                      </rect>
                    );
                  }

                  const yTopo = Math.min(y(ponto.valor), yDaBase);
                  const altura = Math.max(Math.abs(yDaBase - y(ponto.valor)), 1);

                  return (
                    <rect
                      key={serie.analitoId}
                      x={x}
                      y={yTopo}
                      width={larguraDaBarra}
                      height={altura}
                      rx={2}
                      fill={cor}
                      // Contorno âmbar = fora da faixa, na interpretação que o
                      // servidor GRAVOU na emissão. Não é recalculado aqui, e não
                      // diz se subir é bom.
                      className={foraDaFaixa ? 'stroke-amber-500' : undefined}
                      strokeWidth={foraDaFaixa ? 2 : 0}
                    >
                      <title>{tooltip(serie, ponto, grupo.medidoEm)}</title>
                    </rect>
                  );
                })}

                <text
                  x={centro}
                  y={yRotulo}
                  textAnchor={rotuloNaDiagonal ? 'end' : 'middle'}
                  transform={rotuloNaDiagonal ? `rotate(-40 ${centro} ${yRotulo})` : undefined}
                  className="fill-muted-foreground text-[10px]"
                >
                  {formatarData(grupo.medidoEm)}
                  {/* O grupo é UM exame: se ele veio de fora, todas as barras
                      dele vieram. A marca vai na data, que é o rótulo do grupo. */}
                  {grupo.valores.some(ehExterno) && ' ◆'}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <figcaption className="space-y-2">
        <ul className="flex flex-wrap gap-x-4 gap-y-1.5">
          {series.map((serie, indice) => (
            <li key={serie.analitoId} className="flex items-center gap-1.5 text-xs">
              <span
                aria-hidden="true"
                className="inline-block w-3 h-3 rounded-sm shrink-0"
                style={{ backgroundColor: CORES[indice % CORES.length] }}
              />
              <span className="text-foreground">{serie.nome}</span>
              {serie.unidade && <span className="text-muted-foreground">({serie.unidade})</span>}
            </li>
          ))}
        </ul>

        <p className="text-xs text-muted-foreground">
          <span
            aria-hidden="true"
            className="inline-block w-3 h-3 rounded-sm border-2 border-amber-500 align-middle mr-1.5"
          />
          Contorno âmbar: resultado fora da faixa de referência na data em que foi emitido.
          {temUnidadesDiferentes(series) && (
            // Só avisa quando é o caso: no exame de unidade única (o comum) a
            // observação seria ruído.
            <>
              {' '}
              As barras dividem o mesmo eixo — e este exame mistura unidades, então
              compare cada analito com ele mesmo ao longo das datas, não com o vizinho.
            </>
          )}
        </p>

        {grupos.some((grupo) => grupo.valores.some(ehExterno)) && (
          <p className="text-xs text-muted-foreground">
            <span aria-hidden="true" className="mr-1.5">
              ◆
            </span>
            {LEGENDA_DE_EXTERNO}
          </p>
        )}
      </figcaption>
    </figure>
  );
}

/**
 * Vira as séries do avesso: de "um analito, N datas" para "uma data, N analitos".
 *
 * A ordem das datas sai do próprio `pontos`, que o servidor já devolve da mais
 * antiga para a mais recente pela data CLÍNICA (`historico.service.ts`) — não
 * por `criadoEm`. Reordenar aqui reintroduziria o bug do laudo de papel
 * retroativo aparecendo como a última coluna.
 */
function agrupar(series: readonly SerieDoAnalitoResponse[]): Grupo[] {
  const porExame = new Map<string, Grupo>();

  for (const [indice, serie] of series.entries()) {
    for (const ponto of serie.pontos) {
      const existente = porExame.get(ponto.exameId);

      if (existente) {
        existente.valores[indice] = ponto;
        continue;
      }

      const valores: (PontoDoAnalitoResponse | null)[] = Array(series.length).fill(null);
      valores[indice] = ponto;
      porExame.set(ponto.exameId, {
        exameId: ponto.exameId,
        medidoEm: ponto.medidoEm,
        valores,
      });
    }
  }

  return [...porExame.values()].sort((a, b) => a.medidoEm.localeCompare(b.medidoEm));
}

function tooltip(
  serie: SerieDoAnalitoResponse,
  ponto: PontoDoAnalitoResponse | null,
  medidoEm: string,
): string {
  const data = formatarData(medidoEm);

  if (!ponto) return `${serie.nome} — ${data}: não medido neste exame`;

  const valor =
    ponto.valor !== null
      ? `${formatar(ponto.valor)} ${ponto.unidade ?? serie.unidade ?? ''}`.trim()
      : (ponto.valorTexto ?? 'sem valor numérico');

  const externo = rotuloDeExterno(ponto);
  return `${serie.nome} — ${data}: ${valor}${externo ? ` — ${externo}` : ''}`;
}

/** O que o leitor de tela recebe em vez do desenho. */
function resumoAcessivel(
  series: readonly SerieDoAnalitoResponse[],
  grupos: readonly Grupo[],
): string {
  const datas = `${grupos.length} ${grupos.length === 1 ? 'data' : 'datas'}`;
  const analitos = series.map((serie) => serie.nome).join(', ');

  return `Resultados de ${series[0]?.exameNome ?? 'exame'} em ${datas}, uma barra por exame: ${analitos}.`;
}

function temUnidadesDiferentes(series: readonly SerieDoAnalitoResponse[]): boolean {
  return new Set(series.map((serie) => serie.unidade ?? '')).size > 1;
}

function formatar(valor: number): string {
  return valor.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}

function formatarData(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}
